import { inject, Injectable } from '@angular/core';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { groupBy, orderBy, uniqWith } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, tap } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, ExpressionOperator, IntegrationRepository, LoaderService, PatientRepository, SearchFields, SearchItems, StrutEvent } from '../core';
import { DailyEventItem, EventItem, StrutEventExtended, strutEventType } from '../models';


/**
 * This Service handles attachment data.
 */
@Injectable({ providedIn: 'root' })
export class DashboardService {

	private readonly loaderSrv = inject(LoaderService);
	private readonly patRepo = inject(PatientRepository);
	private readonly integrationRepo = inject(IntegrationRepository);

	private readonly _strutEvents = new BehaviorSubject<EventItem[] | null>(null);
	private readonly _dailyEvents = new BehaviorSubject<DailyEventItem[] | null>(null);
	private readonly _selectedDate = new BehaviorSubject<Date | null>(new Date());
	private _fromDate?: Date;
	private _toDate?: Date;

	private readonly _currentPatientList$: BehaviorSubject<SearchItems[]> = new BehaviorSubject<SearchItems[]>([]);
	private _initialPatientList: SearchItems[] = [];
	private _patientId: string | null = null;

	/**
	 * Get current strut events list.
	 */
	get strutEvents$(): Observable<EventItem[] | null> {
		return this._strutEvents.asObservable();
	}

	/**
	 * Get current daily events map.
	 */
	get dailyEvents$(): Observable<DailyEventItem[] | null> {
		return this._dailyEvents.asObservable();
	}

	/**
	 * Get current selected date.
	 */
	get selectedDate$(): Observable<Date | null> {
		return this._selectedDate.asObservable();
	}

	/**
	 * Update current selected date. 
	 */
	updateDate(nextDate?: NgbDate): void {
		const prevDate = fromDate(this._selectedDate.value!);
		if (!!nextDate && !nextDate.equals(prevDate)) {
			const date = toDate(nextDate);
			this._selectedDate.next(date);
			this.updateDailyEvents();
		}
	}

	private updateDailyEvents(): void {
		if (this._strutEvents.value?.length && this._selectedDate.value) {
			const dailyEvents = this.dailyEventsMapper(this._strutEvents.value, this._selectedDate.value);
			this._dailyEvents.next(dailyEvents);
		} else {
			this._dailyEvents.next(null);
		}
	}

	private clearEventList(): void {
		this._selectedDate.next(null);
		this._dailyEvents.next(null);
	}

	/**
	* Load initial patient list for filter.
	*/
	loadPatientList(): void {
		this.patRepo.search({
			top: 0,
			page: 0,
			patientOnly: true,
			deleted: false,
			sortOrder: { name: SearchFields.patNumber, desc: false },
			filters: [{ name: SearchFields.patIsReceived, operator: ExpressionOperator.Ne, value: true }]
		}).pipe(
			map(res => res.result),
			filter(res => !!res),
			map(res => res.items)
		).subscribe(list => {
			this._initialPatientList = list;
			this._currentPatientList$.next([...this._initialPatientList]);
		});
	}

	/**
	* Search for the patient on text changes.
	*/
	filterPatients = (text$: Observable<string>) =>
		text$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			map(term => this._initialPatientList.filter(v => v.patientNumber.toLowerCase().includes(term.toLocaleLowerCase())))
		);

	/**
	 * Get struts events between input dates.
	 */
	private getStrutsEvents(from: Date, to: Date, patientList?: string[]): Observable<StrutEvent[]> {
		return this.integrationRepo.getStrutsEvents(from, to, patientList).pipe(
			map(this.handleApiResponse)
		)
	}

	/**
	 * Update events list according input month.
	 */
	loadEvents(nextMonth: { year: number; month: number }): void {
		this._fromDate = new Date(nextMonth.year, nextMonth.month - 1);
		this._toDate = new Date(nextMonth.year, nextMonth.month);
		this._loadEvents();
	}

	/**
	 * Update events list according input patient.
	 */
	filterEvents(patientId: string | null): void {
		this._patientId = patientId;
		this._loadEvents();
	}

	private _loadEvents(): void {
		if (this._fromDate && this._toDate)
			this.clearEventList();
		this.loaderSrv.show();
		const patientList = this._patientId ? [this._patientId] : undefined;
		this.getStrutsEvents(this._fromDate!, this._toDate!, patientList).pipe(
			map(events => events?.length ? this.eventListMapper(events) : []),
			tap(list => this._strutEvents.next(list)),
			finalize(() => this.loaderSrv.hide())
		).subscribe(() => this.updateDailyEvents());
	}

	/**
	 * Map strut event to calendar event item. 
	 */
	private eventListMapper(events: StrutEventExtended[]): EventItem[] {
		const ordered = orderBy(events, ['dateOfAdjustment']);
		for (const ev of ordered) {
			ev.date = new Date(new Date(ev.dateOfAdjustment).setHours(0, 0, 0, 0));
			ev.type = strutEventType(ev);
			ev.header = `${ev.date}-${ev.type}`;
		}
		const grouped = groupBy(ordered, 'header');
		let result: EventItem[] = [];
		for (const key in grouped) {
			let items = grouped[key];
			if (items.length > 1) {
				items = uniqWith(items, function (item1: StrutEventExtended, item2: StrutEventExtended) {
					return item1.caseId === item2.caseId && item1.header === item2.header;
				});
			}
			result.push({
				date: grouped[key][0].date!,
				type: grouped[key][0].type!,
				items: items
			});
		}
		return result;
	}

	/**
	 * Map calendar event items to dashboard event items.
	 */
	dailyEventsMapper(list: EventItem[], day?: Date): DailyEventItem[] {
		let result = [];
		if (list?.length && day) {
			const filteredItems = list?.filter(i => i.date.getDate() === day.getDate() && i.date.getMonth() === day.getMonth() && i.date.getFullYear() === day.getFullYear());
			const items = filteredItems.flatMap(i => i.items);
			const groupedData = groupBy(items, 'patientNumber');
			for (let key in groupedData) {
				const items = groupedData[key];
				result.push({ patient: key, items: items } as DailyEventItem);
			}
		}
		return result;
	}

	private handleApiResponse<T>(response: ApiResponse<T>) {
		if (response.statusCode == ApiResponseStatusCode.Success) {
			return response.result as T;
		} else {
			throw new Error("Generic error");
		}
	}

	reset(): void {
		this._strutEvents.next(null);
		this._dailyEvents.next(null);
		this._selectedDate.next(new Date());
		this._fromDate = undefined;
		this._toDate = undefined;

		this._currentPatientList$.next([]);
		this._initialPatientList = [];
		this._patientId = null;
	}

}

export function fromDate(date: Date): NgbDate | null {
	return date ? new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate()) : null;
}

export function toDate(date: NgbDateStruct): Date | null {
	return date ? new Date(date.year, date.month - 1, date.day, 0, 0, 0) : null;
}